'use server'

import { apiRequest, ApiResponse } from '../request'

/** De onde sai o valor repassado ao cliente em cada método. */
export type FeeBasis = 'NONE' | 'PROVIDER' | 'CUSTOM'

/**
 * Uma faixa de cobrança. Vale para valores entre `minAmount` e `maxAmount`
 * (nulo = sem teto) e, no crédito, para parcelas entre `fromInstallment` e
 * `toInstallment`. Aplica `fixed` e `percent` SOMADOS — que é como o provedor
 * cobra ("2,69% + R$ 0,29").
 */
export interface FeeRule {
    /** Centavos. */
    minAmount: number
    /** Centavos. `null` = "a partir de minAmount". */
    maxAmount: number | null
    fromInstallment: number
    /** `null` = sem teto de parcelas. */
    toInstallment: number | null
    /** Centavos. */
    fixed: number
    /** 2.69 = 2,69%. */
    percent: number
}

export interface MethodFeePolicy {
    basis: FeeBasis
    /** Usadas quando `basis = CUSTOM`. A primeira que casa vence. */
    rules: FeeRule[]
}

/** Custo de transferir o repasse ao parceiro, diluído por cobrança. */
export interface PayoutFeePolicy {
    enabled: boolean
    fixed: number
    percent: number
}

/** Repasse da antecipação. Percentual MENSAL, proporcional aos dias adiantados. */
export interface AnticipationFeePolicy {
    enabled: boolean
    basis: Exclude<FeeBasis, 'NONE'>
    monthlyPercent: number
}

export interface BillingFeePolicy {
    version: 2
    enabled: boolean
    /**
     * Embute a taxa por dentro (`total = base / (1 - percentual)`) em vez de somar o
     * percentual sobre a base — só assim o líquido cai no valor do serviço, porque a
     * operadora cobra o percentual sobre o total que passou nela.
     */
    grossUp: boolean
    pix: MethodFeePolicy
    creditCard: MethodFeePolicy
    debitCard: MethodFeePolicy
    boleto: MethodFeePolicy
    payout: PayoutFeePolicy
    anticipation: AnticipationFeePolicy
}

const BASE = 'utils/settings/billing/fee-policy'

export async function getBillingFeePolicy(): Promise<
    ApiResponse<BillingFeePolicy>
> {
    return apiRequest<BillingFeePolicy>(BASE, {})
}

export async function updateBillingFeePolicy(
    policy: BillingFeePolicy
): Promise<ApiResponse<BillingFeePolicy>> {
    return apiRequest<BillingFeePolicy>(BASE, { data: policy }, 'patch')
}

/** Conferência de comprovantes anexados às cobranças (leitura por IA). */
export interface ReceiptReviewPolicy {
    /** Aprova a baixa automaticamente quando a IA valida o comprovante. */
    autoApprove: boolean
    /** Confiança mínima (0..1) exigida da IA para aprovar sozinha. */
    minTrustScore: number
}

const RECEIPT_POLICY_BASE = 'utils/settings/billing/receipt-policy'

export async function getReceiptReviewPolicy(): Promise<
    ApiResponse<ReceiptReviewPolicy>
> {
    return apiRequest<ReceiptReviewPolicy>(RECEIPT_POLICY_BASE, {})
}

export async function updateReceiptReviewPolicy(
    policy: ReceiptReviewPolicy
): Promise<ApiResponse<ReceiptReviewPolicy>> {
    return apiRequest<ReceiptReviewPolicy>(
        RECEIPT_POLICY_BASE,
        { data: policy },
        'patch'
    )
}

/**
 * Baixa de pagamentos recebidos por fora do sistema (dinheiro, maquininha, outro).
 * Espelha `OfflineSettlementPolicy` do backend.
 */
export interface OfflineSettlementPolicy {
    /** Marca como paga na hora, sem passar pela fila de aprovação financeira. */
    autoSettle: boolean
}

const OFFLINE_SETTLEMENT_BASE =
    'utils/settings/billing/offline-settlement-policy'

export async function getOfflineSettlementPolicy(): Promise<
    ApiResponse<OfflineSettlementPolicy>
> {
    return apiRequest<OfflineSettlementPolicy>(OFFLINE_SETTLEMENT_BASE, {})
}

export async function updateOfflineSettlementPolicy(
    policy: OfflineSettlementPolicy
): Promise<ApiResponse<OfflineSettlementPolicy>> {
    return apiRequest<OfflineSettlementPolicy>(
        OFFLINE_SETTLEMENT_BASE,
        { data: policy },
        'patch'
    )
}

/** Regra "Nx somente a partir de" (valor total mínimo em centavos). */
export interface InstallmentTier {
    /** Número de parcelas (>= 2). */
    count: number
    /** Valor total mínimo da cobrança (centavos) para liberar este parcelamento. */
    minAmount: number
}

/** Meios de pagamento principais (online) ofertados ao cliente. */
export interface CheckoutPaymentMethods {
    pix: boolean
    creditCard: boolean
    debitCard: boolean
}

/** Configuração de parcelamento no cartão de crédito. */
export interface CheckoutInstallmentPolicy {
    enabled: boolean
    /** Número máximo de parcelas permitido (1..12). */
    maxInstallments: number
    /** Valor mínimo por parcela em centavos (0 = sem mínimo). */
    minInstallmentValue: number
    /** Regras "Nx somente a partir de" (centavos). */
    tiers: InstallmentTier[]
}

export interface CheckoutPaymentPolicy {
    methods: CheckoutPaymentMethods
    installments: CheckoutInstallmentPolicy
}

const PAYMENT_POLICY_BASE = 'utils/settings/billing/payment-policy'

export async function getCheckoutPaymentPolicy(): Promise<
    ApiResponse<CheckoutPaymentPolicy>
> {
    return apiRequest<CheckoutPaymentPolicy>(PAYMENT_POLICY_BASE, {})
}

export async function updateCheckoutPaymentPolicy(
    policy: CheckoutPaymentPolicy
): Promise<ApiResponse<CheckoutPaymentPolicy>> {
    return apiRequest<CheckoutPaymentPolicy>(
        PAYMENT_POLICY_BASE,
        { data: policy },
        'patch'
    )
}

// ====== COBRANÇA PELA IA ======

/**
 * O que o atendente virtual faz quanto a cobrar no agendamento.
 *
 * `DISABLED` e `NEVER` diferem no ALCANCE: nos dois o agendamento nasce sem
 * cobrança, mas `DISABLED` tira da IA a capacidade de cobrar em qualquer
 * contexto — inclusive a venda de mercadoria, que não é agendamento.
 */
export type AiBillingMode = 'DISABLED' | 'ASK' | 'ALWAYS' | 'NEVER'

export interface AiBillingPolicy {
    mode: AiBillingMode
}

const AI_BILLING_POLICY_BASE = 'utils/settings/billing/ai-policy'

export async function getAiBillingPolicy(): Promise<
    ApiResponse<AiBillingPolicy>
> {
    return apiRequest<AiBillingPolicy>(AI_BILLING_POLICY_BASE, {})
}

export async function updateAiBillingPolicy(
    policy: AiBillingPolicy
): Promise<ApiResponse<AiBillingPolicy>> {
    return apiRequest<AiBillingPolicy>(
        AI_BILLING_POLICY_BASE,
        { data: policy },
        'patch'
    )
}

// ====== ESTORNO SEM APROVAÇÃO ======

/**
 * Quem pode estornar devolve o dinheiro na hora, sem abrir solicitação?
 *
 * Espelha `InstantRefundPolicy` do backend. Padrão: desligada — todo estorno passa
 * pela fila de aprovação, com operador eleito e senha.
 */
export interface InstantRefundPolicy {
    allowInstantRefund: boolean
}

const INSTANT_REFUND_POLICY_BASE = 'utils/settings/billing/instant-refund-policy'

export async function getInstantRefundPolicy(): Promise<
    ApiResponse<InstantRefundPolicy>
> {
    return apiRequest<InstantRefundPolicy>(INSTANT_REFUND_POLICY_BASE, {})
}

export async function updateInstantRefundPolicy(
    policy: InstantRefundPolicy
): Promise<ApiResponse<InstantRefundPolicy>> {
    return apiRequest<InstantRefundPolicy>(
        INSTANT_REFUND_POLICY_BASE,
        { data: policy },
        'patch'
    )
}

// ====== REGISTRO DE CAIXA ======

/**
 * Horário de fechamento do caixa diário (`HH:mm`). O que entra a partir dele conta
 * para o dia seguinte. Espelha `CashRegisterPolicy` do backend. Padrão: 18:00.
 */
export interface CashRegisterPolicy {
    closingTime: string
}

const CASH_REGISTER_POLICY_BASE = 'utils/settings/billing/cash-register-policy'

export async function getCashRegisterPolicy(): Promise<
    ApiResponse<CashRegisterPolicy>
> {
    return apiRequest<CashRegisterPolicy>(CASH_REGISTER_POLICY_BASE, {})
}

export async function updateCashRegisterPolicy(
    policy: CashRegisterPolicy
): Promise<ApiResponse<CashRegisterPolicy>> {
    return apiRequest<CashRegisterPolicy>(
        CASH_REGISTER_POLICY_BASE,
        { data: policy },
        'patch'
    )
}

// ====== COBRANÇA DA CONSULTA DE RETORNO ======

/**
 * A consulta marcada como retorno gera cobrança?
 *
 * Espelha `ReturnConsultationBillingPolicy` do backend. Padrão: não cobrar — o
 * retorno é a continuação da consulta já paga, e o agendamento nasce sem
 * cobrança (o valor fica guardado no atendimento).
 */
export interface ReturnConsultationBillingPolicy {
    chargeReturn: boolean
}

const RETURN_CONSULTATION_POLICY_BASE =
    'utils/settings/billing/return-consultation-policy'

export async function getReturnConsultationBillingPolicy(): Promise<
    ApiResponse<ReturnConsultationBillingPolicy>
> {
    return apiRequest<ReturnConsultationBillingPolicy>(
        RETURN_CONSULTATION_POLICY_BASE,
        {}
    )
}

export async function updateReturnConsultationBillingPolicy(
    policy: ReturnConsultationBillingPolicy
): Promise<ApiResponse<ReturnConsultationBillingPolicy>> {
    return apiRequest<ReturnConsultationBillingPolicy>(
        RETURN_CONSULTATION_POLICY_BASE,
        { data: policy },
        'patch'
    )
}

// ====== BASE DA COMISSÃO PERCENTUAL ======

/**
 * Sobre o que a comissão percentual por produtividade incide.
 *
 * Espelha `CommissionBasisPolicy` do backend. Padrão: `REVENUE` (venda total).
 * `MARGIN` é o markup: receita líquida da taxa do gateway menos os repasses.
 */
export type CommissionBasis = 'REVENUE' | 'MARGIN'

export interface CommissionBasisPolicy {
    basis: CommissionBasis
}

const COMMISSION_BASIS_POLICY_BASE =
    'utils/settings/billing/commission-basis-policy'

export async function getCommissionBasisPolicy(): Promise<
    ApiResponse<CommissionBasisPolicy>
> {
    return apiRequest<CommissionBasisPolicy>(COMMISSION_BASIS_POLICY_BASE, {})
}

export async function updateCommissionBasisPolicy(
    policy: CommissionBasisPolicy
): Promise<ApiResponse<CommissionBasisPolicy>> {
    return apiRequest<CommissionBasisPolicy>(
        COMMISSION_BASIS_POLICY_BASE,
        { data: policy },
        'patch'
    )
}

// ====== CUSTO DO ITEM DO CATÁLOGO ======

/**
 * Se o item do catálogo pode ter preço de venda com custo zero.
 *
 * Espelha `CatalogCostPolicy` do backend. Padrão: `false` (custo obrigatório).
 * É da ORGANIZAÇÃO, não da filial: vale para todas as filiais e não entra no
 * "Importar / Aplicar em todas" da aba. A leitura não exige permissão do
 * financeiro, porque o formulário do catálogo também a consulta.
 */
export interface CatalogCostPolicy {
    allowZeroCost: boolean
}

const CATALOG_COST_POLICY_BASE = 'organizations/current/catalog-cost-policy'

export async function getCatalogCostPolicy(): Promise<
    ApiResponse<CatalogCostPolicy>
> {
    return apiRequest<CatalogCostPolicy>(CATALOG_COST_POLICY_BASE, {})
}

export async function updateCatalogCostPolicy(
    policy: CatalogCostPolicy
): Promise<ApiResponse<CatalogCostPolicy>> {
    return apiRequest<CatalogCostPolicy>(
        CATALOG_COST_POLICY_BASE,
        { data: policy },
        'patch'
    )
}

// ====== CONFIGURAÇÃO ENTRE FILIAIS (IMPORTAR / APLICAR EM TODAS) ======
//
// Uma organização com 15 filiais configurava a mesma política 15 vezes, tela por
// tela. A unidade aqui é a ABA — é assim que o operador pensa a configuração
// ("copiar a Cobrança da matriz"), e não política por política.

/** Abas replicáveis. "Taxas Fixas" fica fora: é leitura do custo do provedor. */
export type FinancialSettingsScope =
    | 'checkout'
    | 'fees'
    | 'receiving'
    | 'pricing'

/** O conteúdo de uma aba. Cada escopo preenche os campos que lhe pertencem. */
export interface FinancialTabPayload {
    payment?: CheckoutPaymentPolicy
    aiBilling?: AiBillingPolicy
    returnConsultation?: ReturnConsultationBillingPolicy
    fees?: BillingFeePolicy
    receipt?: ReceiptReviewPolicy
    offline?: OfflineSettlementPolicy
    instantRefund?: InstantRefundPolicy
    cashRegister?: CashRegisterPolicy
    minMarkupPercentage?: number
    commissionBasis?: CommissionBasisPolicy
}

export interface FinancialSettingsBranch {
    id: number
    name: string
    tradingName: string | null
}

const FINANCIAL_SCOPE_BASE = 'utils/settings/financial'

/** Filiais da organização (exceto a atual), para escolher de qual importar. */
export async function getFinancialSettingsBranches(): Promise<
    ApiResponse<FinancialSettingsBranch[]>
> {
    return apiRequest<FinancialSettingsBranch[]>(
        `${FINANCIAL_SCOPE_BASE}/branches`,
        {}
    )
}

/** Lê a configuração de uma aba em outra filial, para importar. */
export async function getFinancialSettingsTab(
    scope: FinancialSettingsScope,
    companyId: number
): Promise<ApiResponse<FinancialTabPayload>> {
    return apiRequest<FinancialTabPayload>(
        `${FINANCIAL_SCOPE_BASE}/tab?scope=${scope}&companyId=${companyId}`,
        {}
    )
}

/** Grava a configuração da aba em todas as filiais ativas da organização. */
export async function applyFinancialSettingsToAll(
    scope: FinancialSettingsScope,
    payload: FinancialTabPayload
): Promise<
    ApiResponse<{ applied: number; companies: { id: number; name: string }[] }>
> {
    return apiRequest<{
        applied: number
        companies: { id: number; name: string }[]
    }>(`${FINANCIAL_SCOPE_BASE}/apply-all`, { data: { scope, payload } }, 'post')
}
